/**
 * Shared Contracts — The universal foundation for all Serenoty API interactions.
 *
 * WHY THIS EXISTS:
 * Every API in the Serenoty ecosystem must speak the same language for errors,
 * pagination, sorting, and filtering. This file defines that language once.
 * All domain contracts extend from these primitives.
 *
 * RULES:
 * - These types are BACKEND-ALIGNED — they mirror what FastAPI will return.
 * - They are IMPLEMENTATION-AGNOSTIC — no fetch, no mock, no adapter logic here.
 * - They are the SINGLE SOURCE OF TRUTH for API shape.
 *
 * BACKEND ALIGNMENT:
 * - Errors follow RFC 9457 Problem Details (app/core/exceptions.py → ProblemDetail)
 * - Pagination supports both cursor-based and offset-based modes
 * - HATEOAS links follow RFC 8288 (app/core/pagination.py → build_hateoas_links)
 *
 * MIGRATION NOTE:
 * Zod schemas are re-exported from auto-generated code (@hey-api/openapi-ts).
 * Types inferred from generated schemas. UI-only constructs remain hand-written.
 */

import { z } from 'zod';
import {
  zProblemDetail as GeneratedProblemDetailSchema,
  zPaginationMeta as GeneratedPaginationMetaSchema,
} from '@/api/generated/hey-api/zod.gen';

// ═══════════════════════════════════════════════════════════════════════
// RFC 9457 — Problem Details for HTTP APIs
// ═══════════════════════════════════════════════════════════════════════

/**
 * RFC 9457 Problem Details — the EXACT shape FastAPI returns for all errors.
 *
 * Backend source: app/core/exceptions.py → ProblemDetail
 * Content-Type: application/problem+json
 *
 * The `type` field is a URI like:
 *   https://api.virza.io/errors/resource-not-found
 *   https://api.virza.io/errors/validation-error
 *   https://api.virza.io/errors/device-limit-exceeded
 *
 * Every adapter (mock + HTTP) MUST produce errors in this shape.
 */
export const ProblemDetailSchema = GeneratedProblemDetailSchema;

export type ProblemDetail = z.infer<typeof ProblemDetailSchema>;

/**
 * Known problem type URIs — mirrors ProblemTypes class in backend.
 * Used as discriminators in error handling.
 *
 * UI-only: These slug constants are not part of the OpenAPI spec.
 */
export const ProblemTypes = {
  // Authentication & Authorization
  AUTHENTICATION_FAILED: 'authentication-failed',
  AUTHORIZATION_DENIED: 'authorization-denied',
  SESSION_EXPIRED: 'session-expired',
  SESSION_SUSPICIOUS: 'session-suspicious',
  ACCOUNT_DELETED: 'account-deleted',
  ACCOUNT_SUSPENDED: 'account-suspended',
  NOT_A_MEMBER: 'not-a-member',
  INSUFFICIENT_ROLE: 'insufficient-role',
  MFA_REQUIRED: 'mfa-required',
  DEVICE_AUTH_FAILED: 'device-auth-failed',

  // Resource errors
  RESOURCE_NOT_FOUND: 'resource-not-found',
  RESOURCE_CONFLICT: 'resource-conflict',
  RESOURCE_DELETED: 'resource-deleted',
  DUPLICATE_IN_WORKSPACE: 'duplicate-in-workspace',

  // Business rules
  DEVICE_LIMIT_EXCEEDED: 'device-limit-exceeded',
  ENROLLMENT_EXPIRED: 'enrollment-expired',
  ENROLLMENT_CODE_INVALID: 'enrollment-code-invalid',
  ENROLLMENT_CODE_REQUIRED: 'enrollment-code-required',
  SECRET_ROTATION_REQUIRED: 'secret-rotation-required',

  // Billing & Plan Tiers
  BILLING_LIMIT_EXCEEDED: 'billing-limit-exceeded',
  PLAN_TIER_INSUFFICIENT: 'plan-tier-insufficient',

  // Rate limiting
  RATE_LIMIT_EXCEEDED: 'rate-limit-exceeded',

  // Validation
  VALIDATION_ERROR: 'validation-error',
  CONSTRAINT_VIOLATION: 'constraint-violation',

  // Infrastructure
  SERVICE_UNAVAILABLE: 'service-unavailable',
  INTERNAL_ERROR: 'internal-error',
} as const;

export type ProblemTypeSlug = (typeof ProblemTypes)[keyof typeof ProblemTypes];

/**
 * Check if a problem type URI ends with a known slug.
 * Backend returns full URIs like "https://api.virza.io/errors/resource-not-found".
 * This helper matches the trailing segment.
 *
 * UI-only: Slug-matching logic for error handling in adapters.
 */
export function matchProblemType(typeUri: string, slug: ProblemTypeSlug): boolean {
  return typeUri.endsWith(`/${slug}`);
}

/**
 * Typed exception class for use within adapters and services.
 * Wraps the raw ProblemDetail from the API with JS Error semantics.
 *
 * UI-only: Error wrapper class not in the OpenAPI spec.
 */
export class ApiError extends Error {
  public readonly type: string;
  public readonly status: number;
  public readonly detail: string | undefined;
  public readonly instance: string | undefined;
  public readonly requestId: string | undefined;
  public readonly errors: Record<string, unknown>[] | undefined;

  constructor(problem: ProblemDetail) {
    super(problem.title);
    this.name = 'ApiError';
    this.type = problem.type;
    this.status = problem.status;
    this.detail = problem.detail ?? undefined;
    this.instance = problem.instance ?? undefined;
    this.requestId = problem.request_id ?? undefined;
    this.errors = problem.errors ?? undefined;
  }

  /** User-facing message: detail if available, otherwise title */
  get userMessage(): string {
    return this.detail ?? this.message;
  }

  /** Check if this error matches a known problem type */
  is(slug: ProblemTypeSlug): boolean {
    return matchProblemType(this.type, slug);
  }
}

/**
 * Factory to create an ApiError from minimal params.
 * Used by mock adapters to simulate realistic backend errors.
 *
 * UI-only: Factory for mock adapter testing.
 */
export function createApiError(
  slug: ProblemTypeSlug,
  title: string,
  status: number,
  detail?: string,
  errors?: Record<string, unknown>[],
): ApiError {
  return new ApiError({
    type: `https://api.virza.io/errors/${slug}`,
    title,
    status,
    detail,
    request_id: crypto.randomUUID(),
    errors,
  });
}

// ═══════════════════════════════════════════════════════════════════════
// Pagination — Dual-mode (offset + cursor)
// ═══════════════════════════════════════════════════════════════════════

/**
 * HATEOAS navigation links — RFC 8288.
 * Backend source: app/core/pagination.py → build_hateoas_links
 *
 * In cursor mode:  self, next?, prev?
 * In offset mode:  self, next?, prev?, first?, last?
 */
export interface HateoasLinks {
  self: string;
  next?: string;
  prev?: string;
  first?: string;
  last?: string;
}

/**
 * Pagination metadata returned with every list response.
 * Supports both cursor and offset pagination modes.
 *
 * Re-exported from generated code.
 */
export const PaginationMetaSchema = GeneratedPaginationMetaSchema;

export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;

/**
 * Universal paginated response envelope.
 * Every list endpoint returns this shape.
 *
 * UI-only: Generic wrapper not directly in the OpenAPI spec
 * (backend uses concrete PaginatedResponse[T] per domain).
 */
export interface PaginatedResponse<T> {
  items: T[];
  pagination: PaginationMeta;
  links?: HateoasLinks;
}

/**
 * Offset-based pagination request params.
 * Sent as query parameters to list endpoints.
 *
 * UI-only: Request param interface for frontend use.
 */
export interface PaginationParams {
  page?: number; // 1-indexed, defaults to 1 (offset mode, deprecated for new endpoints)
  pageSize?: number; // Items per page, defaults to server default
  cursor?: string; // Opaque keyset cursor for efficient deep pagination
}

/**
 * Cursor-based pagination request params.
 * Used for large datasets or real-time feeds.
 *
 * UI-only: Request param interface for frontend use.
 */
export interface CursorPaginationParams {
  cursor?: string; // Opaque cursor from previous response's links.next
  pageSize?: number;
}

// ─── Sorting ───────────────────────────────────────────────────────────
// UI-only: Sorting param interfaces for frontend query composition.

export type SortDirection = 'asc' | 'desc';

export interface SortParams<TField extends string = string> {
  sortBy?: TField;
  sortDirection?: SortDirection;
}

// ─── API Response Envelope ─────────────────────────────────────────────
// UI-only: Generic wrapper for single-item responses.

/**
 * Wrapper for single-item API responses.
 * Provides consistent structure for mutations and detail endpoints.
 */
export interface ApiResponse<T> {
  data: T;
  message?: string;
}

// ─── Timestamps ────────────────────────────────────────────────────────
// UI-only: Timestamp interface for frontend use.

/**
 * Standard timestamp fields present on all persisted entities.
 * ISO 8601 strings to match FastAPI's datetime serialization.
 */
export interface Timestamps {
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

// ─── ID Types ──────────────────────────────────────────────────────────
// UI-only: Branded type for UUIDs.

/** Branded type for UUIDs to prevent raw string usage in critical paths */
export type UUID = string & { readonly __brand: 'UUID' };

// ─── Utility ───────────────────────────────────────────────────────────
// UI-only: Mock adapter helper.

/**
 * Build self-referencing HATEOAS links for mock adapters.
 * Real links come from the backend; this is for mock parity only.
 */
export function buildMockHateoasLinks(
  basePath: string,
  page: number,
  pageSize: number,
  totalPages: number | undefined,
): HateoasLinks {
  const qs = (p: number) => `${basePath}?page=${p}&page_size=${pageSize}`;
  const links: HateoasLinks = { self: qs(page) };

  if (totalPages !== undefined) {
    if (page < totalPages) links.next = qs(page + 1);
    if (page > 1) {
      links.prev = qs(page - 1);
      links.first = qs(1);
    }
    if (totalPages > 1 && page < totalPages) links.last = qs(totalPages);
  }

  return links;
}
